import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Star,
  Search,
  Filter,
  Eye,
  EyeOff,
  Flag,
  Trash2,
  ChevronDown,
  User,
  MessageSquare,
  AlertCircle,
  MoreHorizontal,
  RefreshCw,
  X,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import Pagination from "../../../components/Pagination";

const ReviewList = () => {
  const {
    reviews,
    reviewsPagination,
    fetchReviews,
    hideReview,
    unhideReview,
    flagReview,
    deleteReview,
    loading,
  } = useContext(AdminContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("All");
  const [flaggedFilter, setFlaggedFilter] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [showFlaggedDropdown, setShowFlaggedDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const visibilityDropdownRef = useRef(null);
  const flaggedDropdownRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const loadReviews = () =>
    fetchReviews({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm || undefined,
      isVisible:
        visibilityFilter === "All" ? undefined : visibilityFilter === "Visible",
      flagged:
        flaggedFilter === "All" ? undefined : flaggedFilter === "Flagged",
    });

  useEffect(() => {
    loadReviews();
  }, [currentPage, itemsPerPage, visibilityFilter, flaggedFilter, searchTerm]);

  // Handle search submission
  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  // Handle Enter key press in search input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setVisibilityFilter("All");
    setFlaggedFilter("All");
    setCurrentPage(1);
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      setActiveMenu(null);

      if (
        visibilityDropdownRef.current &&
        !visibilityDropdownRef.current.contains(event.target)
      ) {
        setShowVisibilityDropdown(false);
      }

      if (
        flaggedDropdownRef.current &&
        !flaggedDropdownRef.current.contains(event.target)
      ) {
        setShowFlaggedDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleToggleVisibility = async (review) => {
    try {
      if (review.isVisible) {
        await hideReview(review.id);
      } else {
        await unhideReview(review.id);
      }
      loadReviews();
    } catch (error) {
      // Error already handled in context
    }
  };

  const handleFlag = async (review) => {
    try {
      await flagReview(review.id);
      loadReviews();
    } catch (error) {
      // Error already handled in context
    }
  };

  const handleDelete = async () => {
    if (!selectedReview) return;
    try {
      await deleteReview(selectedReview.id);
      setShowDeleteModal(false);
      setSelectedReview(null);
      loadReviews();
    } catch (error) {
      // Error already handled in context
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={`${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300 dark:text-gray-600"
            }`}
          />
        ))}
      </div>
    );
  };

  const getInitials = (name) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return "#6B7280";
    const colors = [
      "#EF4444",
      "#F59E0B",
      "#10B981",
      "#3B82F6",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
      "#F97316",
      "#84CC16",
      "#6366F1",
    ];
    const index = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text dark:text-dark-text">
          Reviews
        </h1>
        <p className="text-gray-500 dark:text-dark-muted mt-1">
          Manage caregiver reviews and ratings
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex gap-2">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search by caregiver, reviewer, or comment..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full pl-12 pr-4 py-3 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
          />
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearchSubmit}
          className="flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
        >
          <Search size={16} />
        </button>

        {/* Visibility Filter Dropdown */}
        <div className="relative" ref={visibilityDropdownRef}>
          <button
            data-testid="visibility-filter"
            onClick={() => setShowVisibilityDropdown(!showVisibilityDropdown)}
            className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
          >
            <Eye size={16} />
            {visibilityFilter === "All" ? "All Reviews" : visibilityFilter}
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showVisibilityDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showVisibilityDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
              {["All", "Visible", "Hidden"].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setVisibilityFilter(status);
                    setShowVisibilityDropdown(false);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    visibilityFilter === status
                      ? "bg-primary text-white dark:bg-primary"
                      : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                  }`}
                >
                  {status === "All" ? "All Reviews" : status}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Flagged Filter Dropdown */}
        <div className="relative" ref={flaggedDropdownRef}>
          <button
            data-testid="flagged-filter"
            onClick={() => setShowFlaggedDropdown(!showFlaggedDropdown)}
            className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
          >
            <Flag size={16} />
            {flaggedFilter === "All" ? "All" : flaggedFilter}
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showFlaggedDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showFlaggedDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
              {["All", "Flagged", "Unflagged"].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setFlaggedFilter(status);
                    setShowFlaggedDropdown(false);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    flaggedFilter === status
                      ? "bg-primary text-white dark:bg-primary"
                      : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reset Button */}
        <button
          data-testid="clear-filters"
          onClick={handleResetFilters}
          className="flex items-center justify-center px-4 py-3 rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
          title="Reset Filters"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Reviews Table */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-visible">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Caregiver
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Reviewer
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Comment
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <tr
                    key={review.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {review.caregiver?.image ? (
                          <img
                            src={review.caregiver.image}
                            alt={review.caregiver.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-dark-border"
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 border-gray-200 dark:border-dark-border"
                            style={{
                              backgroundColor: getAvatarColor(
                                review.caregiver?.name,
                              ),
                            }}
                          >
                            {getInitials(review.caregiver?.name)}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-text dark:text-dark-text">
                            {review.caregiver?.name || "Unknown"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {review.user?.image ? (
                          <img
                            src={review.user.image}
                            alt={review.user.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-dark-border"
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 border-gray-200 dark:border-dark-border"
                            style={{
                              backgroundColor: getAvatarColor(
                                review.user?.name,
                              ),
                            }}
                          >
                            {getInitials(review.user?.name)}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-text dark:text-dark-text">
                            {review.user?.name || "Anonymous"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {renderStars(review.rating)}
                        <span className="text-sm font-semibold text-gray-700 dark:text-dark-text">
                          {review.rating}/5
                        </span>
                      </div>
                    </td>
                    <td
                      data-testid="view-details-btn"
                      className="px-6 py-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                      onClick={() => {
                        setSelectedReview(review);
                        setShowReviewModal(true);
                      }}
                      title="Click to view full review"
                    >
                      <p className="text-sm text-gray-600 dark:text-dark-muted line-clamp-2 max-w-xs">
                        {review.comment || "No comment"}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            review.isVisible
                              ? "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white"
                              : "bg-gray-600 text-white dark:bg-gray-700 dark:text-white"
                          }`}
                        >
                          {review.isVisible ? (
                            <>
                              <Eye size={12} />
                              Visible
                            </>
                          ) : (
                            <>
                              <EyeOff size={12} />
                              Hidden
                            </>
                          )}
                        </span>
                        {review.flagged && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-600 text-white dark:bg-red-700 dark:text-white">
                            <Flag size={12} />
                            Flagged
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right relative action-menu">
                      <button
                        data-testid="action-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(
                            activeMenu === review.id ? null : review.id,
                          );
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors"
                      >
                        <MoreHorizontal size={18} />
                      </button>

                      {/* Action Dropdown */}
                      {activeMenu === review.id && (
                        <div className="absolute right-6 top-full mt-2 w-56 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 py-1">
                          {/* Toggle Visibility */}
                          <button
                            data-testid="hide-review-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleVisibility(review);
                              setActiveMenu(null);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors ${
                              review.isVisible
                                ? "text-gray-700 dark:text-dark-text"
                                : "text-emerald-600 dark:text-emerald-400"
                            }`}
                          >
                            {review.isVisible ? (
                              <>
                                <EyeOff size={16} />
                                <span>Hide Review</span>
                              </>
                            ) : (
                              <>
                                <Eye size={16} />
                                <span>Show Review</span>
                              </>
                            )}
                          </button>

                          {/* Toggle Flag */}
                          <button
                            data-testid="flag-review-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFlag(review);
                              setActiveMenu(null);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors ${
                              review.flagged
                                ? "text-red-600 dark:text-red-400"
                                : "text-yellow-600 dark:text-yellow-400"
                            }`}
                          >
                            <Flag size={16} />
                            <span>
                              {review.flagged ? "Unflag Review" : "Flag Review"}
                            </span>
                          </button>

                          {/* Divider */}
                          <div className="h-px bg-gray-100 dark:bg-gray-700 my-1"></div>

                          {/* Delete */}
                          <button
                            data-testid="delete-review-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReview(review);
                              setShowDeleteModal(true);
                              setActiveMenu(null);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                          >
                            <Trash2 size={16} />
                            <span>Delete Review</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <MessageSquare
                        size={48}
                        className="text-gray-300 dark:text-gray-600"
                      />
                      <p className="text-gray-500 dark:text-dark-muted">
                        No reviews found
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {reviews.length > 0 && (
          <div className="border-t border-gray-200 dark:border-dark-border bg-gray-50/50 dark:bg-gray-900/30 p-4">
            <Pagination
              currentPage={reviewsPagination.page}
              totalPages={reviewsPagination.totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <AlertCircle
                  className="text-red-600 dark:text-red-400"
                  size={24}
                />
              </div>
              <div>
                <h3 className="text-lg font-bold text-text dark:text-dark-text">
                  Delete Review
                </h3>
                <p className="text-sm text-gray-500 dark:text-dark-muted">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="text-gray-600 dark:text-dark-muted mb-6">
              Are you sure you want to permanently delete this review? This will
              remove it from the caregiver&apos;s profile and cannot be
              recovered.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedReview(null);
                }}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-dark-border text-gray-700 dark:text-dark-text font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
              >
                Delete Review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Comment Modal */}
      {showReviewModal && selectedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl max-w-2xl w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-xl font-bold text-text dark:text-dark-text">
                Review Comment
              </h3>
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedReview(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500 dark:text-dark-muted" />
              </button>
            </div>

            {/* Comment */}
            <div className="mb-6">
              <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg max-h-96 overflow-y-auto">
                <p className="text-gray-700 dark:text-dark-text leading-relaxed whitespace-pre-wrap">
                  {selectedReview.comment || "No comment provided"}
                </p>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-dark-border">
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedReview(null);
                }}
                className="px-6 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewList;
